// Die eigenen Sammlungen (ST-SEA-002 Plan F3, Schnitt 3a; ADR-0065; Bauvorgabe
// DESIGN 3a, Abschnitt 2).
//
// DIE ZAHL KOMMT JE ZEILE: Die Liste traegt keine, weil sie die
// Sichtbarkeitsregel ueber alle Mitglieder jeder Sammlung kostete (2b-3). Jede
// Zeile laedt ihre Zahl ueber die Einzelroute nach, hoechstens vier zugleich,
// in reservierter Breite, damit nichts springt. Scheitert eine, bleibt die
// Uebersicht stehen und die Zeile sagt es in Worten.
//
// GENAU EINE PRIMARY: "Neue Sammlung" im Kopf. Steht der Fehlerzustand mit
// "Erneut versuchen", ist "Neue Sammlung" nicht gerendert.

import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { Body1, Button, Caption1, Card, Skeleton, SkeletonItem, Title3, makeStyles, tokens } from '@fluentui/react-components';

import type { PlainMessage } from '../access.ts';
import {
  collectionHref,
  createdAfterCancelSentence,
  COUNT_CONCURRENCY,
  countLabel,
  createdLabel,
  fetchCollection,
  fetchCollections,
  type CollectionSummary,
  COLLECTIONS_UNREACHABLE,
} from '../collections.ts';
import { useSeitenueberschrift, useUeberschriftImInhalt } from '../shell/layout.tsx';
import { AppLink, navigate } from '../shell/router.tsx';
import { Skelett, SKELETT_SAMMLUNGEN } from '../shell/skelett.tsx';
import { Zustand } from '../shell/zustand.tsx';
import { titelNachAnlageFokussieren } from './sammlung.tsx';
import { ansageNachBeenden } from './sammlung-pflege.tsx';
import { Meldung, NeueSammlungDialog } from './zur-sammlung.tsx';

export interface SammlungenScreenProps {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
}

type Zahl = { readonly art: 'laedt' } | { readonly art: 'da'; readonly zahl: number } | { readonly art: 'fehler' } | { readonly art: 'weg' };

const TITEL_ID = 'sammlungen-titel';


const useStyles = makeStyles({
  kopf: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.5rem 1rem',
  },
  titel: { margin: 0, overflowWrap: 'anywhere' },
  satz: { display: 'block', marginTop: '0.25rem', color: tokens.colorNeutralForeground2 },
  // Eine Karte wie Wiedervorlagen und Kontaktliste (v4 .panel); die Zeilen laufen bis an den Rand.
  karte: {
    marginTop: '1rem',
    padding: 0,
    gap: 0,
    boxShadow: tokens.shadow4,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  liste: { listStyle: 'none', margin: 0, padding: 0 },
  ansage: { display: 'block', marginTop: '0.75rem' },
  // Zustaende in der Karte brauchen den Innenabstand, den die Zeilen selbst tragen.
  leer: { padding: '1rem' },
  zeile: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    ':first-child': { borderTop: 'none' },
    padding: '0.625rem 1rem',
    minHeight: '44px',
    boxSizing: 'border-box',
  },
  name: {
    display: 'block',
    fontWeight: tokens.fontWeightSemibold,
    // Unterstrichen, damit der Link nicht allein an der Farbe haengt (WCAG 1.4.1).
    textDecoration: 'underline',
    overflowWrap: 'anywhere',
  },
  meta: { display: 'block', marginTop: '0.125rem', color: tokens.colorNeutralForeground2, fontVariantNumeric: 'tabular-nums' },
  platzhalter: { display: 'inline-block', width: '6ch', height: '0.75rem', verticalAlign: 'middle' },
  verborgen: { position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' },
  aktionen: { display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '0.75rem' },
});

export function SammlungenScreen({ apiBaseUrl, accessToken, onReauthenticate }: SammlungenScreenProps): ReactElement {
  useUeberschriftImInhalt();
  useSeitenueberschrift('Sammlungen');
  const own = useStyles();
  const [status, setStatus] = useState<'laedt' | 'geladen' | 'abgelehnt'>('laedt');
  const [sammlungen, setSammlungen] = useState<readonly CollectionSummary[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [ladefehler, setLadefehler] = useState<{ readonly message: PlainMessage; readonly weitere: boolean } | undefined>(undefined);
  const [zahlen, setZahlen] = useState<ReadonlyMap<string, Zahl>>(new Map());
  const [dialogOffen, setDialogOffen] = useState(false);
  // Erst nach dem Aushaengen des Dialogs in die neue Sammlung, sonst bleiben
  // Tabster-Platzhalter tabbar unter aria-hidden (axe aria-hidden-focus, A84).
  const [neueSammlung, setNeueSammlung] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (neueSammlung === undefined || dialogOffen) return;
    setNeueSammlung(undefined);
    navigate(collectionHref(neueSammlung));
  }, [dialogOffen, neueSammlung]);
  // Nach dem Beenden einer Sammlung: der Satz und der Fokus auf die h1, einmal.
  const [nachBeenden] = useState(ansageNachBeenden);
  const [ansage, setAnsage] = useState(nachBeenden ?? '');
  const [nachErneut, setNachErneut] = useState(false);
  const angefragt = useRef(new Set<string>());
  const neuKnopf = useRef<HTMLButtonElement | null>(null);
  const laufRef = useRef(0);

  const laden = useCallback(
    async (fortsetzen?: string) => {
      const meinLauf = ++laufRef.current;
      if (fortsetzen === undefined) setStatus('laedt');
      setLadefehler(undefined);
      try {
        const token = await accessToken();
        if (token === undefined) {
          onReauthenticate();
          return;
        }
        const result = await fetchCollections({ baseUrl: apiBaseUrl, accessToken: token, cursor: fortsetzen });
        if (laufRef.current !== meinLauf) return;
        if (result.outcome === 'rejected') {
          setLadefehler({ message: result.message, weitere: fortsetzen !== undefined });
          if (fortsetzen === undefined) setStatus('abgelehnt');
          return;
        }
        setSammlungen((alt) => (fortsetzen === undefined ? result.collections : [...alt, ...result.collections]));
        setCursor(result.nextCursor);
        setStatus('geladen');
      } catch {
        if (laufRef.current !== meinLauf) return;
        setLadefehler({ message: COLLECTIONS_UNREACHABLE, weitere: fortsetzen !== undefined });
        if (fortsetzen === undefined) setStatus('abgelehnt');
      }
    },
    [accessToken, apiBaseUrl, onReauthenticate],
  );

  useEffect(() => {
    void laden();
  }, [laden]);

  useEffect(() => {
    if (nachBeenden !== undefined) document.getElementById(TITEL_ID)?.focus();
  }, [nachBeenden]);

  // Die Zahlen: je Sammlung einmal, hoechstens COUNT_CONCURRENCY gleichzeitig --
  // ueber alle Seiten hinweg. Warteschlange und Zahl der laufenden Abrufe leben
  // deshalb in Refs und nicht im Effekt; sonst brachte "Weitere laden" vier
  // weitere Abrufe neben die noch laufenden.
  const warteschlange = useRef<string[]>([]);
  const laufend = useRef(0);
  useEffect(() => {
    const neu = sammlungen.map((s) => s.collectionId).filter((id) => !angefragt.current.has(id));
    if (neu.length === 0) return;
    for (const id of neu) angefragt.current.add(id);
    warteschlange.current.push(...neu);
    setZahlen((alt) => new Map([...alt, ...neu.map((id) => [id, { art: 'laedt' }] as const)]));
    const arbeiter = async (): Promise<void> => {
      laufend.current += 1;
      try {
        for (let id = warteschlange.current.shift(); id !== undefined; id = warteschlange.current.shift()) {
          let zahl: Zahl;
          try {
            const token = await accessToken();
            const result = token === undefined ? undefined : await fetchCollection({ baseUrl: apiBaseUrl, accessToken: token, collectionId: id });
            zahl =
              result?.outcome === 'loaded' ? { art: 'da', zahl: result.collection.memberCount } : result?.outcome === 'gone' ? { art: 'weg' } : { art: 'fehler' };
          } catch {
            zahl = { art: 'fehler' };
          }
          const fertig = id;
          setZahlen((alt) => new Map(alt).set(fertig, zahl));
        }
      } finally {
        laufend.current -= 1;
      }
    };
    // Nur so viele neue Abrufer, wie unter der Grenze frei sind; laufende holen sich den Rest.
    const frei = Math.min(COUNT_CONCURRENCY - laufend.current, warteschlange.current.length);
    for (let n = 0; n < frei; n += 1) void arbeiter();
  }, [sammlungen, accessToken, apiBaseUrl]);

  const zahlText = (id: string): ReactElement => {
    const zahl = zahlen.get(id) ?? { art: 'laedt' };
    switch (zahl.art) {
      case 'laedt':
        return (
          <>
            <Skeleton aria-hidden="true" className={own.platzhalter}>
              <SkeletonItem shape="rectangle" className={own.platzhalter} />
            </Skeleton>
            <span className={own.verborgen}>Zahl wird geladen</span>
          </>
        );
      case 'da':
        return <>{countLabel(zahl.zahl)}</>;
      case 'fehler':
        return <>Zahl nicht verfügbar</>;
      case 'weg':
        return <>nicht mehr vorhanden</>;
    }
  };

  const fehlerZustand = status === 'abgelehnt' && ladefehler !== undefined && !ladefehler.weitere;

  return (
    <div>
      <div className={own.kopf}>
        <Title3 as="h1" id={TITEL_ID} tabIndex={-1} className={own.titel}>
          Sammlungen
        </Title3>
        {fehlerZustand ? undefined : (
          <Button ref={(knoten: HTMLButtonElement | null) => { neuKnopf.current = knoten; }} appearance="primary" onClick={() => setDialogOffen(true)}>
            Neue Sammlung
          </Button>
        )}
      </div>
      <Body1 className={own.satz}>Deine Sammlungen sind persönlich. Nur du siehst sie.</Body1>
      {/* Immer im DOM, leer ausserhalb des Flusses: so greift die Ansage, wenn eine
          abgebrochene Anlage doch noch ankommt. */}
      <div role="status" aria-live="polite" aria-label="Status: Sammlungen" className={ansage.length === 0 ? own.verborgen : own.ansage}>
        {ansage}
      </div>

      <Card className={own.karte}>
        {status === 'laedt' ? (
          <div className={own.leer}>
            <Skelett ladesatz="Sammlungen werden geladen" zeilen={SKELETT_SAMMLUNGEN} />
          </div>
        ) : undefined}

        {fehlerZustand && ladefehler !== undefined ? (
          <div className={own.leer}>
            <Zustand
              art="fehler"
              titel={ladefehler.message.headline}
              saetze={[ladefehler.message.explanation, ladefehler.message.nextStep]}
              vorgangsnummer={ladefehler.message.correlationId}
              haupt={{
                label: 'Erneut versuchen',
                onClick: () => {
                  setNachErneut(true);
                  void laden();
                },
              }}
              fokus={nachErneut}
            />
          </div>
        ) : undefined}

        {status === 'geladen' && sammlungen.length === 0 ? (
          <div className={own.leer}>
            <Zustand
              art="leer"
              eingebettet
              titel="Noch keine Sammlung"
              saetze={[
                'Eine Sammlung hält Kontakte fest, die du später wieder brauchst, etwa für einen Export.',
                'Such Kontakte und füg sie mit „Zur Sammlung …“ hinzu.',
              ]}
              haupt={{ label: 'Kontakte suchen', href: '/kontakte' }}
            />
          </div>
        ) : undefined}

        {sammlungen.length > 0 ? (
          <ul role="list" className={own.liste} aria-label="Deine Sammlungen">
            {sammlungen.map((sammlung) => (
              <li key={sammlung.collectionId} className={own.zeile}>
                <AppLink to={collectionHref(sammlung.collectionId)} className={own.name}>
                  {sammlung.collectionName}
                </AppLink>
                <Caption1 className={own.meta}>
                  {zahlText(sammlung.collectionId)} · {createdLabel(sammlung.createdAt)}
                </Caption1>
              </li>
            ))}
          </ul>
        ) : undefined}
      </Card>

      {ladefehler?.weitere === true ? <Meldung message={ladefehler.message} lesefehler /> : undefined}
      {status === 'geladen' && cursor !== undefined ? (
        <div className={own.aktionen}>
          <Button appearance="secondary" onClick={() => void laden(cursor)}>
            {ladefehler?.weitere === true ? 'Erneut laden' : 'Weitere laden'}
          </Button>
        </div>
      ) : undefined}

      {dialogOffen ? (
        <NeueSammlungDialog
          apiBaseUrl={apiBaseUrl}
          accessToken={accessToken}
          onReauthenticate={onReauthenticate}
          onSchliessen={() => {
            setDialogOffen(false);
            setTimeout(() => neuKnopf.current?.focus(), 0);
          }}
          onAngelegt={(sammlung) => {
            titelNachAnlageFokussieren();
            setDialogOffen(false);
            setNeueSammlung(sammlung.collectionId);
          }}
          onNachAbbruchAngelegt={(sammlung) => {
            setAnsage(createdAfterCancelSentence(sammlung.collectionName, 'uebersicht'));
            void laden();
          }}
        />
      ) : undefined}
    </div>
  );
}
